import { UserManager, WebStorageStateStore, type User } from "oidc-client-ts";

const issuer = import.meta.env.VITE_OIDC_ISSUER as string;
const clientId = import.meta.env.VITE_OIDC_CLIENT_ID as string;

/**
 * Authorization Code + PKCE against Keycloak.
 *
 * `bandcamp-web` is a public client, so there is no secret to protect; PKCE is
 * what stops an intercepted authorization code being redeemed by anyone else.
 *
 * Tokens live in sessionStorage rather than localStorage: it is scoped to the
 * tab and cleared when the tab closes, which is the smaller blast radius of the
 * two. Neither survives XSS, so the real defence is that this app renders no
 * untrusted HTML.
 */
export const userManager = new UserManager({
  authority: issuer,
  client_id: clientId,
  redirect_uri: `${window.location.origin}/auth/callback`,
  post_logout_redirect_uri: window.location.origin,
  response_type: "code",
  scope: "openid profile email",
  userStore: new WebStorageStateStore({ store: window.sessionStorage }),
  // Renew in the background a minute before the 15-minute token dies, so a
  // long browse never lands on a 401.
  automaticSilentRenew: true,
  accessTokenExpiringNotificationTimeInSeconds: 60,
  // Keycloak's end-session endpoint wants the id_token to log out cleanly.
  revokeTokensOnSignout: true,
});

export async function signIn(): Promise<void> {
  // Come back to where the reader was, not to the front page.
  await userManager.signinRedirect({
    state: { returnTo: window.location.pathname + window.location.search },
  });
}

export async function signOut(): Promise<void> {
  await userManager.signoutRedirect();
}

/** Completes the redirect back from Keycloak. Returns where to go next. */
export async function completeSignIn(): Promise<string> {
  // The same route serves the silent renew, which loads it in a hidden
  // iframe when there is no refresh token to use. That iframe must answer
  // its parent and stop, not render the whole app inside itself and time
  // out -- which signed the reader out on Safari, where the iframe is also
  // where third-party storage rules bite.
  if (window.self !== window.top) {
    await userManager.signinSilentCallback();
    return "/";
  }
  const user = await userManager.signinRedirectCallback();
  const state = user.state as { returnTo?: string } | undefined;
  return state?.returnTo || "/";
}

export async function currentUser(): Promise<User | null> {
  const user = await userManager.getUser();
  if (!user || user.expired) return null;
  return user;
}
